import React, { useMemo } from "react";
import { Box, Theme, Tooltip, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { i18n } from "i18next";

import { toISOPercent } from "components/formatting/ISOPercentage";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import PercentChangeTooltip from "./PercentChangeTooltip";
import { defaultSettings } from "./PercentChangeSettings";
import WidgetAlert, { WidgetErrorType } from "components/widgetParts/WidgetAlert";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from "system/widget/Widget";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import FittedText from "components/formatting/FittedText";

export function calculatePercentChange(values: Array<number | string>): number {
  const splitIndex = Math.ceil(values.length / 2) - 1;
  const isSplitPointShared = values.length % 2 === 1;
  let firstHalf = 0;
  let secondHalf = 0;
  let sum = 0;
  for (let i=0; i<values.length; i++) {
    let value = values[i];
    if (typeof value === "string") {
      // It is possible that the API returns floating point values.  JavaScript interprets these
      // which will end un concatenating them together instead of adding them.  By parsing them
      // into a float they will be consudered a number that will then be added correctly.
      value = parseFloat(value);
    }
    if (i <= splitIndex) {
      firstHalf += value;
    } else if (i > splitIndex) {
      secondHalf += value;
    }
    if (i === splitIndex && isSplitPointShared) {
      secondHalf += value;
    }
    sum += value;
  }
  const percentChange = (secondHalf - firstHalf) / firstHalf;
  return percentChange || 0;
}

export function toPercentChangeString(i18n: i18n, value: number): string {
  const isoNumberString = toISOPercent(i18n, Math.abs(value));
  let directionIcon = "→";
  if (value > 0) {
    directionIcon = "↑";
  } else if (value < 0) {
    directionIcon = "↓";
  }
  const percentChangeString = directionIcon + isoNumberString;
  return percentChangeString;
}

export function getPercentChangeTextColor(theme: Theme, value: number): string {
  let textColor = theme.colors.namedColors.neutral;
  if (value > 0) {
    textColor = theme.colors.namedColors.positive;
  } else if (value < 0) {
    textColor = theme.colors.namedColors.negative;
  }
  return textColor;
}

function PercentChangeWidget(props: WithQueryWidgetProps): React.JSX.Element {
  const theme = useTheme();
  const {i18n} = useTranslation();

  const [hovered, setHovered] = React.useState<boolean>(false);

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'percent-change');
  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'percent-change');
  const apiResponse = apiResponseInfo?.responseBody || {};

  const dataSeries = useMemo(() => {
    if (!originalRequest || !apiResponse) {
      return [];
    }
    const firstAggKey = Object.keys(originalRequest.requestInit?.body.aggs || {})[0];
    const responseAgg = apiResponse[firstAggKey];
    return responseAgg?.series[responseAgg.series.length-1] ?? [];
  }, [apiResponse, originalRequest]);

  const percentChange = calculatePercentChange(dataSeries.values);
  const percentChangeString = toPercentChangeString(i18n, percentChange);
  const textColor = getPercentChangeTextColor(theme, percentChange);
  const currentState = {...defaultSettings, ...props.widget?.data?.settings};

  return (
    <WidgetLayout
      header={
        <WidgetTitle
          alignment={currentState.widgetTitleAlignment}
          customTitle={currentState.customWidgetTitle}
          defaultTitle={dataSeries.label}
          settingsTitle={currentState.widgetTitle}
          widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
      }
    >
      <>
        {!!percentChange &&
          <Tooltip
            title={(hovered) ? <PercentChangeTooltip dataSeries={dataSeries} color={textColor} /> : ""}
            open={hovered}
            followCursor={true}
          >
            <Box sx={{
                height: "100%",
                position: "relative",
              }}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}>
              <FittedText content={percentChangeString} color={textColor} horizontalAlignment="center" />
            </Box>
          </Tooltip>
        }
        {!percentChange &&
          <WidgetAlert type={WidgetErrorType.NO_DATA} />
        }
      </>
    </WidgetLayout>
  );
}

export default withQueryWidget(PercentChangeWidget, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
